import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { useDispatch } from 'react-redux';

import {
    getGetAdminPricingQueryKey,
    getListUsersAdministrationQueryKey,
    getUpdateUsersAdministrationMutationOptions,
    updateUsersAdministration,
    UserRole,
    type AccessStatus,
    type AdministrationUser,
} from '@/modules/api/codegen';
import { actions as messagesActions } from '@/modules/messages';
import { loadSettings } from '@/modules/settings/services/actions';
import { isLastAdminRemovalError } from '@/services/utilities/isApiError';

export type UpdateUsersData =
    | {
          users: AdministrationUser[];
          status: AccessStatus;
          role: UserRole;
      }
    | {
          users: AdministrationUser[];
          status: AccessStatus;
      };
export const useUpdateUsers = () => {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    // Active user count feeds per-user Stripe pricing, so the list, the pricing and the settings always refresh together.
    const refreshUsersAndPricing = async () => {
        await queryClient.invalidateQueries({ queryKey: getListUsersAdministrationQueryKey() });
        await queryClient.invalidateQueries({ queryKey: getGetAdminPricingQueryKey() });
        dispatch(loadSettings());
    };

    const updateUsersMutation = useMutation({
        mutationKey: getUpdateUsersAdministrationMutationOptions().mutationKey,
        mutationFn: async (data: UpdateUsersData) => {
            if ('role' in data) {
                return updateUsersAdministration({
                    userIds: data.users.map(user => user.id!),
                    role: data.role,
                    status: data.status,
                });
            }

            // BE doesn't allow updating just status without role, so when we need to batch update status of users
            // with different roles, we need to send two separate requests to update them.
            const adminUsers = data.users.filter(user => user.role === UserRole.admin);
            const regularUsers = data.users.filter(user => user.role === UserRole.user);

            const promises = [];

            if (!isEmpty(adminUsers)) {
                promises.push(
                    updateUsersAdministration({
                        userIds: adminUsers.map(user => user.id!),
                        role: UserRole.admin,
                        status: data.status,
                    }),
                );
            }

            if (!isEmpty(regularUsers)) {
                promises.push(
                    updateUsersAdministration({
                        userIds: regularUsers.map(user => user.id!),
                        role: UserRole.user,
                        status: data.status,
                    }),
                );
            }

            return Promise.all(promises);
        },
        onSuccess: async (_, { users }) => {
            await refreshUsersAndPricing();

            dispatch(
                messagesActions.displaySuccessMessage({
                    message: { id: 'user-administration.table.actions.success', values: { numberOfRecords: users?.length } },
                    options: { duration: 5 },
                }),
            );
        },
        onError: async error => {
            const isLastAdminRemoval = isLastAdminRemovalError(error);

            if (isLastAdminRemoval) {
                // A mixed-role batch is sent as two requests, so one of them can have been applied before the other was rejected.
                await refreshUsersAndPricing();
            }

            dispatch(
                messagesActions.displayErrorMessage({
                    message: {
                        id: isLastAdminRemoval
                            ? 'user-administration.table.actions.error.lastAdmin'
                            : 'user-administration.table.actions.error',
                    },
                    options: { duration: 5 },
                }),
            );
        },
    });

    return updateUsersMutation;
};
